
import { Component, Inject, Prop, Vue } from "vue-property-decorator";
import { Container } from "inversify";
import { MergerAcquisitionsListStore } from "../../stores/mergerAcquisitions/MergerAcquisitionsListStore";
import { MergerAcquisition } from "@gsx/common";
import { enrichedListColumns } from "../../utils/tableUtils";
import NoRecords from "../../../../common/src/components/NoRecords.vue";
import LoadingOverlay from "../../../../common/src/components/LoadingOverlay.vue";
import { GeneralTransactionType } from "@gsx/common";
import { hasQuiltyEnterpriseSubscription } from "../../../../common/src/utility/entity/user";
import { AuthStore } from "../../../../common/src/stores/AuthStore";

interface Column {
    id?: keyof (MergerAcquisition & {
        "acquirerOrganization.organizationName": string;
        "targetOrganization.organizationName": string;
    });
    name: string;
    colWidth?: string;
}

@Component({
    components: { NoRecords, LoadingOverlay },
})
export default class TransactionTable extends Vue {
    @Inject("container") readonly container!: Container;

    readonly mergerAcquisitionsListStore: MergerAcquisitionsListStore = this.container.get(
        MergerAcquisitionsListStore,
    );
    readonly authStore: AuthStore = this.container.get(AuthStore);

    @Prop({ default: false }) limitedDisplay!: boolean;

    isInvestments =
        this.mergerAcquisitionsListStore.generalTransactionType ===
        GeneralTransactionType.MergerAcquisition
            ? false
            : true;

    showFullProfileContent = "";
    mergerAcquisitionsColumns: ReadonlyArray<Column> = [
        {
            id: "announceDate",
            name: "Date",
            colWidth: this.limitedDisplay
                ? "width: 3%; text-align: center;"
                : "width: 9%; text-align: left;",
        },
        {
            id: "acquirerOrganization.organizationName",
            name: "Acquirer",
            colWidth: this.limitedDisplay
                ? "width: 9%; text-align: center;"
                : "width: 9%; text-align: left;",
        },
        {
            id: "targetOrganization.organizationName",
            name: "Target",
            colWidth: this.limitedDisplay
                ? "width: 9%; text-align: center;"
                : "width: 9%; text-align: left;",
        },
        {
            name: "Status",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "Transaction E.V. (M)",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "Currency",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "Target Country",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "Target Sector",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "E.V./EBITDA",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "Trailing Rev (M)",
            colWidth: "width: 9%; text-align: center;",
        },
        {
            name: "Trailing EBITDA (M)",
            colWidth: "width: 9%; text-align: center;",
        },
    ];
    investmentColumns: ReadonlyArray<Column> = [
        {
            id: "announceDate",
            name: "Date",
            colWidth: "width: 8.5%; text-align: left;",
        },
        {
            id: "targetOrganization.organizationName",
            name: "Target",
            colWidth: "width: 16.5%; text-align: left;",
        },
        {
            name: "Transaction Subtype",
            colWidth: "width: 12.5%; text-align: left;",
        },
        {
            name: "Transaction Value (M)",
            colWidth: "width: 12.5%; text-align: center;",
        },
        {
            name: "Currency",
            colWidth: "width: 12.5%; text-align: center;",
        },
        {
            name: "Target Country",
            colWidth: "width: 12.5%; text-align: center;",
        },
        {
            name: "Target Sector",
            colWidth: "width: 12.5%; text-align: center;",
        },
        {
            name: "Investors",
            colWidth: "width: 12.5%; text-align: center;",
        },
    ];
    investmentColumnsLimited: ReadonlyArray<Column> = [
        {
            id: "announceDate",
            name: "Date",
            colWidth: "width: 3%; text-align: center",
        },
        {
            id: "targetOrganization.organizationName",
            name: "Target",
            colWidth: "width: 9%; text-align: center",
        },
        {
            name: "Investors",
            colWidth: "width: 9%; text-align: center",
        },
    ];
    get enrichedMergerAcquisitionColumns() {
        if (this.limitedDisplay) {
            this.mergerAcquisitionsColumns = this.mergerAcquisitionsColumns.slice(0, 3);
        }
        return enrichedListColumns(
            this.mergerAcquisitionsListStore,
            this.mergerAcquisitionsColumns,
        );
    }
    get enrichedInvestmentColumns() {
        if (this.limitedDisplay) {
            return enrichedListColumns(
                this.mergerAcquisitionsListStore,
                this.investmentColumnsLimited,
            );
        }
        return enrichedListColumns(this.mergerAcquisitionsListStore, this.investmentColumns);
    }

    toggleProfileFullContent(mergerAcquisitionId: string) {
        if (this.showFullProfileContent === mergerAcquisitionId) {
            this.showFullProfileContent = "";
        } else {
            this.showFullProfileContent = mergerAcquisitionId;
        }
    }

    dropDownTypeChanged(event: any) {
        switch (event.target.value) {
            case "Investments": {
                this.isInvestments = true;
                this.mergerAcquisitionsListStore.generalTransactionType =
                    GeneralTransactionType.Investment;

                break;
            }
            case "M&A": {
                this.isInvestments = false;
                this.mergerAcquisitionsListStore.generalTransactionType =
                    GeneralTransactionType.MergerAcquisition;
                break;
            }
            default: {
                this.isInvestments = false;
                this.mergerAcquisitionsListStore.generalTransactionType =
                    GeneralTransactionType.MergerAcquisition;
                break;
            }
        }
        this.mergerAcquisitionsListStore.reload();
    }

    get hasExportAcess() {
        return hasQuiltyEnterpriseSubscription(this.authStore.user);
    }

    exportToCSV() {
        let csvContent = "";
        const items = this.mergerAcquisitionsListStore.items;
        if (!items) return;

        // Check which table is active
        if (!this.isInvestments) {
            const headers = this.enrichedMergerAcquisitionColumns.map((column) => column.name);
            csvContent += headers.join(",") + "\n";

            items.forEach((item) => {
                const row = [
                    item.announceDate,
                    item.acquirerOrganization?.organizationName,
                    item.targetOrganization?.organizationName,
                    item.status,
                    item.transactionEnterpriseValue,
                    item.transactionCurrency,
                    item.targetCountry,
                    item.targetGeneralSectorCategory,
                    item.enterpriseValue,
                    item.trailingRev,
                    item.trailingEbitda,
                ].map((field) => `"${field || "N/A"}"`);
                csvContent += row.join(",") + "\n";
            });
        } else {
            const headers = this.enrichedInvestmentColumns.map((column) => column.name);
            csvContent += headers.join(",") + "\n";

            items.forEach((item) => {
                const row = [
                    item.announceDate,
                    item.targetOrganization?.organizationName,
                    item.transactionSubtype,
                    item.investmentAmount,
                    item.transactionCurrency,
                    item.targetCountry,
                    item.targetGeneralSectorCategory,
                    item.investors,
                ].map((field) => `"${field || "N/A"}"`);
                csvContent += row.join(",") + "\n";
            });
        }

        // Create a blob and download
        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);
        link.setAttribute("href", url);
        link.setAttribute("download", "deals-export.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    }
}
